import { useState } from "react";
import { websiteService } from "../../services/websiteService";
import { useSiteContent } from "../siteContent";
import { ChoiceButton, Field, Notice, fieldClass } from "../components/fields";

function formatTrackWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function StatusRow({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#5d6b62]">{label}</dt>
      <dd className="mt-0.5 whitespace-pre-wrap break-words text-sm text-[#16301f]">{value}</dd>
    </div>
  );
}

function ComplaintStatus({ result }) {
  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      <p className="text-sm font-semibold text-[#16301f]">
        {result.reference} is {result.status_label}.
      </p>
      <dl className="mt-3 space-y-2.5">
        <StatusRow label="Customer" value={result.customer_name} />
        <StatusRow label="LR / shipment number" value={result.lr_number} />
        <StatusRow label="Booking date" value={result.booking_date} />
        <StatusRow label="Problem" value={result.problem_type} />
        <StatusRow label="Branch" value={result.branch_name} />
        <StatusRow label="What happened" value={result.issue} />
        <StatusRow label="Internal note" value={result.internal_note} />
      </dl>
      {(result.updates || []).length > 0 && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#5d6b62]">History</p>
          <ul className="mt-2 space-y-2">
            {result.updates.map((update, index) => (
              <li key={`${update.created_at}-${index}`} className="rounded-lg bg-[#f4f7f5] px-3 py-2">
                <p className="text-sm font-medium text-[#16301f]">
                  {update.label}
                  {update.status_label ? ` · ${update.status_label}` : ""}
                </p>
                {update.note ? <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-600">{update.note}</p> : null}
                {(update.created_at || update.actor_name) ? (
                  <p className="mt-1 text-xs text-slate-400">
                    {update.created_at ? formatTrackWhen(update.created_at) : ""}
                    {update.actor_name ? `${update.created_at ? " · " : ""}${update.actor_name}` : ""}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const emptyForm = {
  lr: "",
  bookingDate: "",
  branch: "",
  issue: "",
  name: "",
  mobile: "",
  email: "",
};

export default function Complaints() {
  const { company, complaintTypes } = useSiteContent();
  const [problem, setProblem] = useState("");
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState("");
  const [statusResult, setStatusResult] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!problem) return;
    setError("");
    setSending(true);
    try {
      const response = await websiteService.submitComplaint({ ...form, problem });
      setSubmitted(response.data?.data || { reference: "" });
    } catch (err) {
      setError(err.response?.data?.error || "We could not register that complaint. Please call the helpline.");
    } finally {
      setSending(false);
    }
  };

  const handleStatus = async (event) => {
    event.preventDefault();
    const value = reference.trim();
    if (!value) return;
    try {
      const response = await websiteService.complaintStatus(value);
      setStatusResult(response.data?.data || null);
      setStatusMessage("");
    } catch (err) {
      setStatusResult(null);
      setStatusMessage(err.response?.data?.error || `No complaint matches ${value}. Call ${company.phone} if you need help.`);
    }
  };

  return (
    <div className="bg-[#0B6A34]">
      <section className="container-page py-14 text-white sm:py-16">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#b7e7a8]">Complaints</p>
        <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight sm:text-5xl">
          If something went wrong, tell us here
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">
          Register your complaint with the LR number. It goes to the branch and to head office, and you can check its status below at any time.
        </p>
      </section>

      <section className="container-page grid gap-6 pb-16 lg:grid-cols-[1fr_280px]">
        {submitted ? (
          <Notice>
            <p className="font-semibold">Thanks, {form.name}. Your complaint is registered.</p>
            <p className="mt-2">
              Reference {submitted.reference}. Keep this number to check the status. You can also call {company.phone} with LR {form.lr}.
            </p>
          </Notice>
        ) : (
          <form onSubmit={handleSubmit} className="rounded-xl bg-white p-5 sm:p-6">
            <fieldset>
              <legend className="text-base font-bold text-[#16301f]">1. What is the problem?</legend>
              <div className="mt-3 flex flex-wrap gap-2">
                {complaintTypes.map((item) => (
                  <ChoiceButton key={item} selected={problem === item} onClick={() => setProblem(item)}>
                    {item}
                  </ChoiceButton>
                ))}
              </div>
            </fieldset>

            <fieldset className="mt-6">
              <legend className="text-base font-bold text-[#16301f]">2. Which consignment?</legend>
              <div className="mt-3 grid gap-4 sm:grid-cols-3">
                <Field label="LR number">
                  <input name="lr" required value={form.lr} onChange={update} placeholder="Shipment number on your bill" className={fieldClass} />
                </Field>
                <Field label="Booking date">
                  <input name="bookingDate" type="date" required value={form.bookingDate} onChange={update} className={fieldClass} />
                </Field>
                <Field label="Branch involved (if known)">
                  <input name="branch" value={form.branch} onChange={update} placeholder="Booking or delivery branch" className={fieldClass} />
                </Field>
              </div>
            </fieldset>

            <fieldset className="mt-6">
              <legend className="text-base font-bold text-[#16301f]">3. What happened?</legend>
              <Field label="Describe the issue" className="mt-3">
                <textarea name="issue" required rows={4} value={form.issue} onChange={update} placeholder="What did you expect, what happened, and when?" className={fieldClass} />
              </Field>
              <label className="mt-4 block">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#5d6b62]">Attach photos or documents (optional)</span>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={(event) => setFiles(Array.from(event.target.files || []).map((file) => file.name))}
                  className="mt-1.5 block w-full text-sm text-slate-600"
                />
                <span className="mt-1 block text-xs text-slate-500">
                  Share damage photos with the branch on WhatsApp or when they call. This form records the complaint itself.
                </span>
                {files.length > 0 && <span className="mt-1 block text-xs text-[#0B6A34]">{files.join(", ")}</span>}
              </label>
            </fieldset>

            <fieldset className="mt-6">
              <legend className="text-base font-bold text-[#16301f]">4. Who should we contact?</legend>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Field label="Your name">
                  <input name="name" required value={form.name} onChange={update} placeholder="Full name" className={fieldClass} />
                </Field>
                <Field label="Mobile number">
                  <input name="mobile" required inputMode="tel" value={form.mobile} onChange={update} placeholder="10-digit mobile" className={fieldClass} />
                </Field>
                <Field label="Email (optional)" className="sm:col-span-2">
                  <input name="email" type="email" value={form.email} onChange={update} placeholder="name@company.com" className={fieldClass} />
                </Field>
              </div>
            </fieldset>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={!problem || sending}
                className="rounded-md bg-[#0B6A34] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#085628] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? "Registering…" : "Register complaint"}
              </button>
              <p className="text-xs text-slate-500">The LR number is the shipment number. It must belong to a consignment booked on the date you enter.</p>
            </div>
          </form>
        )}

        <aside className="space-y-4">
          <form onSubmit={handleStatus} className="rounded-xl bg-white p-5">
            <h2 className="text-base font-bold text-[#16301f]">Check complaint status</h2>
            <Field label="Complaint reference" className="mt-3">
              <input value={reference} onChange={(event) => setReference(event.target.value)} placeholder="Enter your reference" className={fieldClass} />
            </Field>
            <button type="submit" className="mt-3 w-full rounded-md bg-[#6fbf45] py-2.5 text-sm font-semibold text-white hover:bg-[#5eaa38]">
              Check status
            </button>
            {statusMessage && <p className="mt-3 text-sm leading-relaxed text-slate-600">{statusMessage}</p>}
            {statusResult && <ComplaintStatus result={statusResult} />}
          </form>

          <div className="rounded-xl bg-[#e7f3ea] p-5 text-sm leading-relaxed text-[#16301f]">
            <h2 className="text-base font-bold">How we handle it</h2>
            <ol className="mt-3 space-y-2">
              <li><span className="font-bold text-[#0B6A34]">1</span> Registered. You receive a reference number.</li>
              <li><span className="font-bold text-[#0B6A34]">2</span> Acknowledged. The branch confirms it received your complaint.</li>
              <li><span className="font-bold text-[#0B6A34]">3</span> Investigated. We trace the consignment and speak to both branches.</li>
              <li><span className="font-bold text-[#0B6A34]">4</span> Resolved. We tell you the outcome and any settlement.</li>
            </ol>
          </div>

          <div className="rounded-xl border border-white/25 p-5 text-sm text-white">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#b7e7a8]">Not resolved?</p>
            <p className="mt-2 leading-relaxed text-white/85">
              Call the cargo helpline on {company.phone} or write to {company.email}. Escalation contact details will be added here.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}
